<!-- 'default' hides itself on a single page. 'directory' stays put whenever
     there are results, because its "Showing x to y of z …" line carries the
     filtered count that the page relies on. -->
<nav class="pagination-container"
     [class.pagination-container--directory]="isDirectory"
     aria-label="Pagination Navigation"
     *ngIf="totalPages > 1 || (isDirectory && totalItems > 0)">
      <ul class="pagination">
        <!-- First Page — 'default' hides it on page 1 (you're already there);
             'directory' keeps it in place, greyed, per the wireframe. -->
        <li class="page-item"
            *ngIf="showFirstLast && (isDirectory || currentPage > 1)"
            [ngClass]="{ 'disabled': currentPage === 1 }">
          <a class="page-link"
             *ngIf="currentPage > 1"
             [routerLink]="baseRoute"
             [queryParams]="getQueryParams(1)"
             (click)="onPageClick(1, $event)"
             title="Go to first page">
            <ng-container *ngTemplateOutlet="firstIcon"></ng-container>
            <span class="sr-only">First</span>
          </a>
          <span class="page-link disabled-link" *ngIf="currentPage === 1" aria-disabled="true">
            <ng-container *ngTemplateOutlet="firstIcon"></ng-container>
            <span class="sr-only">First</span>
          </span>
        </li>

        <!-- Previous Page -->
        <li class="page-item" [ngClass]="{ 'disabled': currentPage === 1 }" *ngIf="showPrevNext">
          <a class="page-link"
             *ngIf="currentPage > 1"
             [routerLink]="baseRoute"
             [queryParams]="getQueryParams(currentPage - 1)"
             (click)="onPageClick(currentPage - 1, $event)"
             title="Go to previous page">
            <ng-container *ngTemplateOutlet="prevIcon"></ng-container>
            <span class="sr-only">Previous</span>
          </a>
          <span class="page-link disabled-link" *ngIf="currentPage === 1" aria-disabled="true">
            <ng-container *ngTemplateOutlet="prevIcon"></ng-container>
            <span class="sr-only">Previous</span>
          </span>
        </li>

        <!-- Page Numbers -->
        <li class="page-item"
            *ngFor="let page of visiblePages"
            [ngClass]="{
              'active': page === currentPage,
              'disabled': page === '...'
            }">
          <!-- Regular page number -->
          <a class="page-link"
             *ngIf="page !== '...'"
             [routerLink]="baseRoute"
             [queryParams]="getQueryParams(page)"
             (click)="onPageClick(page, $event)"
             [title]="'Go to page ' + page"
             [attr.aria-label]="'Go to page ' + page"
             [attr.aria-current]="page === currentPage ? 'page' : null">
            {{ page }}
          </a>

          <!-- Ellipsis -->
          <span class="page-link ellipsis" *ngIf="page === '...'">
            {{ page }}
          </span>
        </li>

        <!-- Next Page -->
        <li class="page-item" [ngClass]="{ 'disabled': currentPage === totalPages }" *ngIf="showPrevNext">
          <a class="page-link"
             *ngIf="currentPage < totalPages"
             [routerLink]="baseRoute"
             [queryParams]="getQueryParams(currentPage + 1)"
             (click)="onPageClick(currentPage + 1, $event)"
             title="Go to next page">
            <ng-container *ngTemplateOutlet="nextIcon"></ng-container>
            <span class="sr-only">Next</span>
          </a>
          <span class="page-link disabled-link" *ngIf="currentPage === totalPages" aria-disabled="true">
            <ng-container *ngTemplateOutlet="nextIcon"></ng-container>
            <span class="sr-only">Next</span>
          </span>
        </li>

        <!-- Last Page — 'default' hides it on the last page; 'directory'
             keeps it in place, greyed. -->
        <li class="page-item"
            *ngIf="showFirstLast && (isDirectory || currentPage < totalPages)"
            [ngClass]="{ 'disabled': currentPage === totalPages }">
          <a class="page-link"
             *ngIf="currentPage < totalPages"
             [routerLink]="baseRoute"
             [queryParams]="getQueryParams(totalPages)"
             (click)="onPageClick(totalPages, $event)"
             title="Go to last page">
            <ng-container *ngTemplateOutlet="lastIcon"></ng-container>
            <span class="sr-only">Last</span>
          </a>
          <span class="page-link disabled-link" *ngIf="currentPage === totalPages" aria-disabled="true">
            <ng-container *ngTemplateOutlet="lastIcon"></ng-container>
            <span class="sr-only">Last</span>
          </span>
        </li>
      </ul>

      <!-- Page Info -->
      <div class="pagination-info" *ngIf="showPageInfo">
        <span>
          Showing {{ getStartItem() }} to {{ getEndItem() }} of {{ totalItems }} {{ itemLabel }}<span *ngIf="itemSuffix"> {{ itemSuffix }}</span>
        </span>
      </div>
    </nav>

<!-- Edge-button glyphs. The directory variant uses the wireframe's chevron
     SVGs; the default variant keeps the guillemets the job and candidate
     listings already render. -->
<ng-template #firstIcon>
  <svg *ngIf="isDirectory" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true" focusable="false">
    <polyline points="11 17 6 12 11 7" /><polyline points="18 17 13 12 18 7" />
  </svg>
  <span *ngIf="!isDirectory" aria-hidden="true">&laquo;&laquo;</span>
</ng-template>

<ng-template #prevIcon>
  <svg *ngIf="isDirectory" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true" focusable="false">
    <polyline points="15 18 9 12 15 6" />
  </svg>
  <span *ngIf="!isDirectory" aria-hidden="true">&laquo;</span>
</ng-template>

<ng-template #nextIcon>
  <svg *ngIf="isDirectory" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true" focusable="false">
    <polyline points="9 18 15 12 9 6" />
  </svg>
  <span *ngIf="!isDirectory" aria-hidden="true">&raquo;</span>
</ng-template>

<ng-template #lastIcon>
  <svg *ngIf="isDirectory" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true" focusable="false">
    <polyline points="13 17 18 12 13 7" /><polyline points="6 17 11 12 6 7" />
  </svg>
  <span *ngIf="!isDirectory" aria-hidden="true">&raquo;&raquo;</span>
</ng-template>
